import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { AppLayout } from '@/components/AppLayout';
import { StallForm } from '@/components/marketplace/StallForm';
import { ProductForm } from '@/components/marketplace/ProductForm';
import { CheckoutDialog } from '@/components/marketplace/CheckoutDialog';
import { BrowseMarketplace } from '@/components/marketplace/BrowseMarketplace';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Store, Package, Plus, Pencil, Trash2, ShoppingCart, Eye, Code2,
  Copy, Check, AlertTriangle,
} from 'lucide-react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useMyStalls } from '@/hooks/useMyStalls';
import { useMyProducts } from '@/hooks/useMyProducts';
import { useToast } from '@/hooks/useToast';
import { LoginArea } from '@/components/auth/LoginArea';
import {
  type Stall,
  type Product,
  stallToContent,
  productToContent,
  formatPrice,
} from '@/lib/marketplace';
import type { NostrEvent } from '@nostrify/nostrify';

// ── Cart state ───────────────────────────────────────────────────────────────
interface CartItem { product: Product; quantity: number }

function useCart() {
  const [cart, setCart] = useState<CartItem[]>([]);
  const add = (product: Product) =>
    setCart(c => {
      const existing = c.find(ci => ci.product.id === product.id);
      if (existing) return c.map(ci => ci.product.id === product.id ? { ...ci, quantity: ci.quantity + 1 } : ci);
      return [...c, { product, quantity: 1 }];
    });
  const remove = (productId: string) => setCart(c => c.filter(ci => ci.product.id !== productId));
  const clear = () => setCart([]);
  const total = cart.reduce((s, i) => s + i.quantity, 0);
  return { cart, add, remove, clear, total };
}

// ── Event JSON viewer dialog ──────────────────────────────────────────────────
function EventJsonDialog({
  event, open, onOpenChange,
}: {
  event: NostrEvent | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [copied, setCopied] = useState(false);
  const json = event ? JSON.stringify(event, null, 2) : '';
  const copy = async () => {
    await navigator.clipboard.writeText(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code2 className="h-4 w-4 text-primary" />
            Merchant Event JSON
            <Button variant="ghost" size="sm" className="ml-auto h-7 gap-1 text-xs" onClick={copy}>
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[400px] w-full rounded border bg-muted/50">
          <pre className="text-[11px] p-4 leading-relaxed font-mono whitespace-pre-wrap break-all">
            {json}
          </pre>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// ── Stall card (My Stalls tab) ────────────────────────────────────────────────
function StallCard({
  event, stall, selected, onSelect, onEdit, onDelete, onViewJson,
}: {
  event: NostrEvent;
  stall: Stall;
  selected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onViewJson: () => void;
}) {
  return (
    <Card
      className={`cursor-pointer transition-all hover:shadow-md ${selected ? 'border-primary ring-1 ring-primary' : ''}`}
      onClick={onSelect}
    >
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-semibold truncate">{stall.name}</CardTitle>
            <CardDescription className="text-xs mt-0.5 line-clamp-2">
              {stall.description || 'No description'}
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-[10px] shrink-0">{stall.currency}</Badge>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-3">
        <div className="flex flex-wrap gap-1">
          {stall.shipping.map(z => (
            <Badge key={z.id} variant="secondary" className="text-[10px] px-1.5">
              {z.name || 'Zone'}: {formatPrice(z.cost, stall.currency)}
            </Badge>
          ))}
        </div>
        <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
          <Button variant="outline" size="sm" className="h-7 gap-1 text-xs flex-1" onClick={onEdit}>
            <Pencil className="h-3 w-3" />Edit
          </Button>
          <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={onViewJson}>
            <Code2 className="h-3 w-3" />JSON
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Product card (My Products tab) ────────────────────────────────────────────
function ProductCard({
  event, product, stall, onAddToCart, onEdit, onDelete, onViewJson,
}: {
  event: NostrEvent;
  product: Product;
  stall: Stall;
  onAddToCart: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onViewJson: () => void;
}) {
  return (
    <Card className="transition-all hover:shadow-md">
      {product.images?.[0] && (
        <div className="aspect-video w-full overflow-hidden rounded-t-lg bg-muted">
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover"
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        </div>
      )}
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-semibold truncate">{product.name}</CardTitle>
            <CardDescription className="text-xs mt-0.5 line-clamp-2">
              {product.description || 'No description'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-base font-bold text-primary">
            {formatPrice(product.price, stall.currency)}
          </span>
          <Badge
            variant={product.quantity === null ? 'secondary' : product.quantity > 0 ? 'outline' : 'destructive'}
            className="text-[10px]"
          >
            {product.quantity === null ? '∞ Unlimited' : product.quantity > 0 ? `${product.quantity} left` : 'Out of stock'}
          </Badge>
        </div>

        {Array.isArray(product.specs) && product.specs.length > 0 && (
          <div className="space-y-0.5">
            {(product.specs as unknown as string[][]).slice(0, 3).map(([k, v], i) => (
              <div key={i} className="flex gap-1 text-[10px] text-muted-foreground">
                <span className="font-medium">{k}:</span><span>{v}</span>
              </div>
            ))}
          </div>
        )}

        {product.categories && product.categories.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {product.categories.map(cat => (
              <Badge key={cat} variant="secondary" className="text-[9px] px-1.5 py-0">
                #{cat}
              </Badge>
            ))}
          </div>
        )}

        <div className="flex gap-1.5">
          <Button
            size="sm"
            className="h-7 gap-1 text-xs flex-1"
            onClick={onAddToCart}
            disabled={product.quantity !== null && product.quantity <= 0}
          >
            <ShoppingCart className="h-3 w-3" />Add to Cart
          </Button>
          <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={onEdit}>
            <Pencil className="h-3 w-3" />Edit
          </Button>
          <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={onViewJson}>
            <Code2 className="h-3 w-3" />JSON
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export function MarketplacePage() {
  useSeoMeta({ title: 'Marketplace — Aeon', description: 'NIP-15 Nostr Marketplace Stall Manager' });

  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const { toast } = useToast();

  const { data: stalls = [], isLoading: stallsLoading, refetch: refetchStalls } = useMyStalls();
  const [selectedStallId, setSelectedStallId] = useState<string | undefined>();

  const selectedStallEntry = stalls.find(s => s.stall.id === selectedStallId) ?? stalls[0];
  const activeStall = selectedStallEntry?.stall;

  const { data: products = [], isLoading: productsLoading, refetch: refetchProducts } = useMyProducts(activeStall?.id);

  const [view, setView] = useState<'stalls' | 'products' | 'browse'>('stalls');

  // Stall form state
  const [editingStall, setEditingStall] = useState<Stall | undefined>(undefined);
  const [showStallForm, setShowStallForm] = useState(false);
  const [isSavingStall, setIsSavingStall] = useState(false);

  // Product form state
  const [editingProduct, setEditingProduct] = useState<Product | undefined>(undefined);
  const [showProductForm, setShowProductForm] = useState(false);
  const [isSavingProduct, setIsSavingProduct] = useState(false);

  // JSON viewer
  const [jsonEvent, setJsonEvent] = useState<NostrEvent | null>(null);
  const [jsonOpen, setJsonOpen] = useState(false);

  // My-stalls cart (for testing your own store)
  const { cart, add: addToCart, remove: removeFromCart, clear: clearCart, total: cartTotal } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  // ── Stall publish ─────────────────────────────────────────────────────────
  const handleSaveStall = async (stall: Stall) => {
    setIsSavingStall(true);
    try {
      await publishEvent({
        kind: 30017,
        content: stallToContent(stall),
        tags: [
          ['d', stall.id],
          ['alt', `Nostr Marketplace Stall: ${stall.name}`],
        ],
      });
      toast({ title: 'Stall published!', description: `"${stall.name}" is live on Nostr.` });
      setShowStallForm(false);
      setEditingStall(undefined);
      await refetchStalls();
    } catch (err) {
      toast({ title: 'Failed to publish stall', description: (err as Error).message, variant: 'destructive' });
    } finally {
      setIsSavingStall(false);
    }
  };

  // ── Product publish ───────────────────────────────────────────────────────
  const handleSaveProduct = async (product: Product) => {
    if (!activeStall) return;
    setIsSavingProduct(true);
    try {
      const tags: string[][] = [
        ['d', product.id],
        ['alt', `Nostr Marketplace Product: ${product.name}`],
      ];
      if (product.categories) {
        for (const cat of product.categories) tags.push(['t', cat]);
      }
      await publishEvent({
        kind: 30018,
        content: productToContent(product),
        tags,
      });
      toast({ title: 'Product listed!', description: `"${product.name}" is live on your stall.` });
      setShowProductForm(false);
      setEditingProduct(undefined);
      await refetchProducts();
    } catch (err) {
      toast({ title: 'Failed to list product', description: (err as Error).message, variant: 'destructive' });
    } finally {
      setIsSavingProduct(false);
    }
  };

  // ── Delete via kind:5 ─────────────────────────────────────────────────────
  const handleDeleteEvent = async (event: NostrEvent, label: string) => {
    if (!confirm(`Delete "${label}"? This publishes a kind:5 deletion request.`)) return;
    try {
      await publishEvent({
        kind: 5,
        content: '',
        tags: [
          ['e', event.id],
          ['a', `${event.kind}:${event.pubkey}:${event.tags.find(t => t[0] === 'd')?.[1] ?? ''}`],
        ],
      });
      toast({ title: 'Deletion published' });
      await refetchStalls();
      await refetchProducts();
    } catch (err) {
      toast({ title: 'Delete failed', description: (err as Error).message, variant: 'destructive' });
    }
  };

  // ── Logged-out gate ───────────────────────────────────────────────────────
  if (!user) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto py-16 text-center space-y-4">
          <Store className="h-12 w-12 text-muted-foreground mx-auto" />
          <h1 className="text-2xl font-bold">Nostr Marketplace</h1>
          <p className="text-muted-foreground text-sm max-w-md mx-auto">
            Create stalls and list products using NIP-15. Send orders via encrypted DMs.
            Log in to manage your store, or browse the public marketplace below.
          </p>
          <LoginArea className="mx-auto max-w-xs" />
          <div className="pt-4 border-t max-w-4xl mx-auto">
            <p className="text-xs text-muted-foreground mb-4">
              You can still browse public stalls without logging in:
            </p>
            <BrowseMarketplace onViewJson={e => { setJsonEvent(e); setJsonOpen(true); }} />
          </div>
          <EventJsonDialog event={jsonEvent} open={jsonOpen} onOpenChange={setJsonOpen} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto space-y-5">

        {/* ── Page header ── */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Store className="h-5 w-5 text-primary" />
              Marketplace
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              NIP-15 · kind:30017 stalls · kind:30018 products · type:0 encrypted orders
            </p>
          </div>

          {/* My-stalls cart shortcut */}
          {cartTotal > 0 && activeStall && view === 'products' && (
            <Button variant="default" size="sm" className="gap-2" onClick={() => setCheckoutOpen(true)}>
              <ShoppingCart className="h-4 w-4" />
              Cart ({cartTotal})
            </Button>
          )}
        </div>

        {/* ── Main tabs ── */}
        <Tabs value={view} onValueChange={v => setView(v as typeof view)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="stalls" className="gap-1.5 text-xs sm:text-sm">
              <Store className="h-3.5 w-3.5" />
              My Stalls
              {stalls.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-[10px] px-1 h-4">{stalls.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="products" className="gap-1.5 text-xs sm:text-sm" disabled={stalls.length === 0}>
              <Package className="h-3.5 w-3.5" />
              Products
              {products.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-[10px] px-1 h-4">{products.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="browse" className="gap-1.5 text-xs sm:text-sm">
              <Eye className="h-3.5 w-3.5" />
              Browse
            </TabsTrigger>
          </TabsList>

          {/* ── MY STALLS TAB ── */}
          <TabsContent value="stalls" className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">Your Stalls (kind:30017)</h2>
              <Button
                size="sm"
                className="gap-2 h-8"
                onClick={() => { setEditingStall(undefined); setShowStallForm(true); }}
              >
                <Plus className="h-3.5 w-3.5" />New Stall
              </Button>
            </div>

            {showStallForm ? (
              <StallForm
                initial={editingStall}
                onSave={handleSaveStall}
                onCancel={() => { setShowStallForm(false); setEditingStall(undefined); }}
                isSaving={isSavingStall}
              />
            ) : stallsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[1, 2].map(i => (
                  <Card key={i}>
                    <CardContent className="p-4 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                      <Skeleton className="h-8 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : stalls.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center space-y-3">
                  <Store className="h-10 w-10 text-muted-foreground mx-auto" />
                  <p className="text-sm font-medium">No stalls yet</p>
                  <p className="text-xs text-muted-foreground">
                    Create your first stall to start listing products on Nostr
                  </p>
                  <Button
                    size="sm"
                    className="gap-2"
                    onClick={() => { setEditingStall(undefined); setShowStallForm(true); }}
                  >
                    <Plus className="h-4 w-4" />Create Your First Stall
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {stalls.map(({ event, stall }) => (
                  <StallCard
                    key={stall.id}
                    event={event}
                    stall={stall}
                    selected={activeStall?.id === stall.id}
                    onSelect={() => { setSelectedStallId(stall.id); setView('products'); }}
                    onEdit={() => { setEditingStall(stall); setShowStallForm(true); }}
                    onDelete={() => handleDeleteEvent(event, stall.name)}
                    onViewJson={() => { setJsonEvent(event); setJsonOpen(true); }}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── MY PRODUCTS TAB ── */}
          <TabsContent value="products" className="space-y-4 mt-4">
            {/* Stall picker when user has multiple stalls */}
            {stalls.length > 1 && (
              <div className="flex gap-2 flex-wrap">
                {stalls.map(({ stall }) => (
                  <button
                    key={stall.id}
                    onClick={() => setSelectedStallId(stall.id)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      activeStall?.id === stall.id
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {stall.name}
                  </button>
                ))}
              </div>
            )}

            {activeStall ? (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-semibold">{activeStall.name} — Products (kind:30018)</h2>
                    <p className="text-xs text-muted-foreground">Currency: {activeStall.currency}</p>
                  </div>
                  <Button
                    size="sm"
                    className="gap-2 h-8"
                    onClick={() => { setEditingProduct(undefined); setShowProductForm(true); }}
                  >
                    <Plus className="h-3.5 w-3.5" />List Product
                  </Button>
                </div>

                {showProductForm ? (
                  <ProductForm
                    stall={activeStall}
                    initial={editingProduct}
                    onSave={handleSaveProduct}
                    onCancel={() => { setShowProductForm(false); setEditingProduct(undefined); }}
                    isSaving={isSavingProduct}
                  />
                ) : productsLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map(i => (
                      <Card key={i}>
                        <CardContent className="p-4 space-y-2">
                          <Skeleton className="h-32 w-full rounded" />
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-8 w-full" />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : products.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="py-12 text-center space-y-3">
                      <Package className="h-10 w-10 text-muted-foreground mx-auto" />
                      <p className="text-sm font-medium">No products in this stall</p>
                      <p className="text-xs text-muted-foreground">List your first product to start selling</p>
                      <Button
                        size="sm"
                        className="gap-2"
                        onClick={() => { setEditingProduct(undefined); setShowProductForm(true); }}
                      >
                        <Plus className="h-4 w-4" />List Your First Product
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {products.map(({ event, product }) => (
                      <ProductCard
                        key={product.id}
                        event={event}
                        product={product}
                        stall={activeStall}
                        onAddToCart={() => addToCart(product)}
                        onEdit={() => { setEditingProduct(product); setShowProductForm(true); }}
                        onDelete={() => handleDeleteEvent(event, product.name)}
                        onViewJson={() => { setJsonEvent(event); setJsonOpen(true); }}
                      />
                    ))}
                  </div>
                )}

                {/* My-stalls cart float */}
                {cartTotal > 0 && (
                  <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
                    <Card className="shadow-xl border-primary/30 bg-card/95 backdrop-blur">
                      <CardContent className="py-2.5 px-4 flex items-center gap-4">
                        <ShoppingCart className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">
                          {cartTotal} item{cartTotal !== 1 ? 's' : ''} in cart
                        </span>
                        <Button size="sm" className="h-7 gap-1 text-xs" onClick={() => setCheckoutOpen(true)}>
                          Checkout
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={clearCart}>
                          Clear
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </>
            ) : (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>Create a stall first before listing products.</AlertDescription>
              </Alert>
            )}
          </TabsContent>

          {/* ── BROWSE TAB ── */}
          <TabsContent value="browse" className="mt-4">
            <BrowseMarketplace
              onViewJson={e => { setJsonEvent(e); setJsonOpen(true); }}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* ── My-stalls checkout dialog ── */}
      {checkoutOpen && activeStall && cart.length > 0 && (
        <CheckoutDialog
          open={checkoutOpen}
          onOpenChange={v => { setCheckoutOpen(v); if (!v) clearCart(); }}
          merchantPubkey={user.pubkey}
          stall={activeStall}
          cart={cart}
        />
      )}

      {/* ── JSON viewer ── */}
      <EventJsonDialog event={jsonEvent} open={jsonOpen} onOpenChange={setJsonOpen} />
    </AppLayout>
  );
}
